import React, { useState, useEffect } from "react";
import axios from "axios";
import html2pdf from "html2pdf.js";
import {
  Gift, Plus, Calendar, Users, Trash2, Pencil, ArrowLeft, CheckCircle2,
  Clock, Coins, X, Save, DollarSign, FileDown
} from "lucide-react";
import { toast } from "sonner";
import { formatBRDate } from "../utils/date";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  name: "",
  description: "",
  event_date: "",
  due_date: "",
  member_price: 0,
  adult_guest_price: 0,
  minor_guest_price: 0,
  cost_center_id: "cc_01",
  category: "Eventos & Cobranças Avulsas",
  subcategory: "",
  participants: {}, // { member_id: { adults, minors, include_member } }
};

export const EventsView = ({ members, costCenters, formatCurrency }) => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState({});

  useEffect(() => {
    axios.get(`${API}/finance/subcategories`)
      .then((r) => setCatalog(r.data?.catalog || {}))
      .catch(() => setCatalog({}));
  }, []);

  const reloadEvents = async () => {
    try {
      const res = await axios.get(`${API}/treasury/events`);
      setEvents(res.data || []);
    } catch (err) {
      toast.error("Falha ao carregar eventos.");
    }
  };

  const reloadDetail = async (id) => {
    if (!id) return;
    try {
      const res = await axios.get(`${API}/treasury/events/${id}`);
      setSelectedDetail(res.data);
    } catch {
      toast.error("Falha ao carregar cobranças.");
    }
  };

  useEffect(() => { reloadEvents(); }, []);
  useEffect(() => {
    if (selectedEventId) reloadDetail(selectedEventId);
    else setSelectedDetail(null);
  }, [selectedEventId]);

  const openForm = (ev = null) => {
    if (ev) {
      setEditingEvent(ev);
      setForm({
        name: ev.name || "",
        description: ev.description || "",
        event_date: ev.event_date || "",
        due_date: ev.due_date || "",
        member_price: ev.member_price || 0,
        adult_guest_price: ev.adult_guest_price || 0,
        minor_guest_price: ev.minor_guest_price || 0,
        cost_center_id: ev.cost_center_id || "cc_01",
        category: ev.category || "Eventos & Cobranças Avulsas",
        subcategory: ev.subcategory || "",
        participants: {},
      });
    } else {
      setEditingEvent(null);
      setForm({ ...emptyForm, participants: {} });
    }
    setShowForm(true);
  };

  const submitEvent = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Informe o nome do evento.");
    setBusy(true);
    try {
      const { participants, ...rest } = form;
      const participantsList = Object.entries(participants || {})
        .filter(([, p]) => p && p.selected)
        .map(([member_id, p]) => ({
          member_id,
          adults: parseInt(p.adults) || 0,
          minors: parseInt(p.minors) || 0,
          include_member: p.include_member !== false,
        }));
      const payload = {
        ...rest,
        member_price: parseFloat(form.member_price) || 0,
        adult_guest_price: parseFloat(form.adult_guest_price) || 0,
        minor_guest_price: parseFloat(form.minor_guest_price) || 0,
        participants: participantsList.length > 0 ? participantsList : null,
      };
      if (editingEvent) {
        await axios.patch(`${API}/treasury/events/${editingEvent.id}`, payload);
        toast.success("Evento atualizado.");
      } else {
        const res = await axios.post(`${API}/treasury/events`, payload);
        const auto = res.data?.auto_charges || 0;
        toast.success(auto > 0 ? `Evento criado + ${auto} cobrança(s) lançadas.` : "Evento criado.");
      }
      setShowForm(false);
      setEditingEvent(null);
      reloadEvents();
      if (selectedEventId) reloadDetail(selectedEventId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao salvar.");
    } finally {
      setBusy(false);
    }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Remover o evento "${ev.name}"? Todas as cobranças e receitas geradas por ele serão apagadas.`)) return;
    try {
      await axios.delete(`${API}/treasury/events/${ev.id}`);
      toast.success("Evento removido.");
      if (selectedEventId === ev.id) setSelectedEventId(null);
      reloadEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao remover.");
    }
  };

  // ---- lista principal ----
  if (!selectedEventId) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-serif font-bold text-white text-lg">Eventos & Cobranças Avulsas</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Festa de Natal, Confraternizações, Rateios — cobrança por Irmão + convidados adultos/menores.
            </p>
          </div>
          <button
            data-testid="btn-new-event"
            onClick={() => openForm()}
            className="inline-flex items-center gap-1.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-2 rounded-lg text-xs uppercase tracking-wider"
          >
            <Plus className="w-3.5 h-3.5" />
            Novo Evento
          </button>
        </div>

        {showForm && (
          <EventForm
            form={form}
            setForm={setForm}
            editingEvent={editingEvent}
            costCenters={costCenters}
            catalog={catalog}
            members={members}
            onCancel={() => { setShowForm(false); setEditingEvent(null); }}
            onSubmit={submitEvent}
            busy={busy}
          />
        )}

        {events.length === 0 ? (
          <div className="p-8 text-center rounded-xl border border-dashed border-slate-700 text-slate-400 text-sm" data-testid="events-empty">
            Nenhum evento cadastrado ainda. Clique em <strong>"Novo Evento"</strong> para começar.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-grid">
            {events.map((ev) => (
              <div key={ev.id} className="luxury-card p-4 border-l-4 border-l-purple-500 space-y-2 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-purple-400" />
                    <span className="font-bold text-white text-sm">{ev.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openForm(ev)} className="p-1 rounded-md border border-slate-700 text-slate-400 hover:text-white" title="Editar evento">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteEvent(ev)} className="p-1 rounded-md border border-rose-500/30 text-rose-400 hover:bg-rose-500/10" title="Remover">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {ev.description && <p className="text-xs text-slate-400">{ev.description}</p>}
                <div className="text-[10px] text-slate-500 space-y-0.5">
                  {ev.event_date && <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Evento: {formatBRDate(ev.event_date)}</div>}
                  {ev.due_date && <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> Vencimento: {formatBRDate(ev.due_date)}</div>}
                </div>
                <div className="grid grid-cols-3 gap-1 text-[10px] pt-1">
                  <div className="p-1 bg-slate-800/60 rounded text-center">
                    <div className="text-slate-500">Irmão</div>
                    <div className="text-[#D4AF37] font-mono font-bold">{formatCurrency(ev.member_price)}</div>
                  </div>
                  <div className="p-1 bg-slate-800/60 rounded text-center">
                    <div className="text-slate-500">Adulto</div>
                    <div className="text-[#D4AF37] font-mono font-bold">{formatCurrency(ev.adult_guest_price)}</div>
                  </div>
                  <div className="p-1 bg-slate-800/60 rounded text-center">
                    <div className="text-slate-500">Menor</div>
                    <div className="text-[#D4AF37] font-mono font-bold">{formatCurrency(ev.minor_guest_price)}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-slate-800">
                  <div className="text-[10px] text-slate-500">
                    <span className="text-emerald-400 font-bold">{ev.paid_count || 0}</span> pagos • <span className="text-amber-400 font-bold">{(ev.total_charges || 0) - (ev.paid_count || 0)}</span> abertos
                  </div>
                  <button
                    data-testid={`btn-open-event-${ev.id}`}
                    onClick={() => setSelectedEventId(ev.id)}
                    className="bg-[#D4AF37]/20 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-slate-950 font-bold px-2.5 py-1 rounded-md text-[10px] transition-all"
                  >
                    Abrir Cobranças
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- detalhe do evento ----
  return (
    <EventDetailView
      detail={selectedDetail}
      members={members}
      costCenters={costCenters}
      formatCurrency={formatCurrency}
      onBack={() => setSelectedEventId(null)}
      onReload={() => reloadDetail(selectedEventId)}
      onEdit={() => selectedDetail && openForm(selectedDetail.event)}
    />
  );
};

// -----------------------------------------------------------------------------
// Sub-componente: formulário de evento
// -----------------------------------------------------------------------------
const EventForm = ({ form, setForm, editingEvent, costCenters, catalog = {}, members = [], onCancel, onSubmit, busy }) => {
  const categoryOptions = Object.keys(catalog || {});
  const subcategoryOptions = (catalog && catalog[form.category]) || [];

  // Helpers para editar linha de participante
  const memberPrice = parseFloat(form.member_price) || 0;
  const adultPrice = parseFloat(form.adult_guest_price) || 0;
  const minorPrice = parseFloat(form.minor_guest_price) || 0;

  const getP = (id) => (form.participants || {})[id] || { selected: false, adults: 0, minors: 0, include_member: true };
  const setP = (id, patch) => setForm({ ...form, participants: { ...(form.participants || {}), [id]: { ...getP(id), ...patch } } });
  const rowTotal = (p) => (p.include_member ? memberPrice : 0) + (parseInt(p.adults) || 0) * adultPrice + (parseInt(p.minors) || 0) * minorPrice;

  const selectedIds = Object.entries(form.participants || {}).filter(([, p]) => p && p.selected).map(([id]) => id);
  const totalGeral = selectedIds.reduce((s, id) => s + rowTotal(getP(id)), 0);

  const selectAll = () => {
    const anySelected = selectedIds.length > 0;
    const next = {};
    members.forEach((m) => {
      next[m.id] = { ...getP(m.id), selected: !anySelected || selectedIds.length < members.length };
    });
    setForm({ ...form, participants: next });
  };

  const fmt = (v) => `R$ ${(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
  <form onSubmit={onSubmit} className="luxury-card p-4 space-y-3 border border-[#D4AF37]/40" data-testid="event-form">
    <div className="flex items-center justify-between border-b border-slate-800 pb-2">
      <span className="font-serif font-bold text-sm text-white flex items-center gap-2">
        <Gift className="w-4 h-4 text-[#D4AF37]" />
        {editingEvent ? "Editar Evento" : "Novo Evento"}
      </span>
      <button type="button" onClick={onCancel} className="text-slate-400 hover:text-white">
        <X className="w-4 h-4" />
      </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
      <div className="md:col-span-2">
        <label className="block text-slate-400 mb-1">Nome do Evento *</label>
        <input data-testid="input-event-name" type="text" required value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700"
          placeholder="Ex: Festa de Natal 2026" />
      </div>
      <div className="md:col-span-2">
        <label className="block text-slate-400 mb-1">Descrição (opcional)</label>
        <input type="text" value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700" />
      </div>
      <div>
        <label className="block text-slate-400 mb-1">Data do Evento</label>
        <input type="date" value={form.event_date}
          onChange={(e) => setForm({ ...form, event_date: e.target.value })}
          className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700" />
      </div>
      <div>
        <label className="block text-slate-400 mb-1">Vencimento do Pagamento</label>
        <input data-testid="input-event-due-date" type="date" value={form.due_date}
          onChange={(e) => setForm({ ...form, due_date: e.target.value })}
          className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700" />
      </div>
      <div>
        <label className="block text-slate-400 mb-1">Valor por Irmão (R$)</label>
        <input data-testid="input-event-member-price" type="number" step="0.01" min="0"
          value={form.member_price}
          onChange={(e) => setForm({ ...form, member_price: e.target.value })}
          className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700 font-mono" />
      </div>
      <div>
        <label className="block text-slate-400 mb-1">Convidado Adulto (R$)</label>
        <input data-testid="input-event-adult-price" type="number" step="0.01" min="0"
          value={form.adult_guest_price}
          onChange={(e) => setForm({ ...form, adult_guest_price: e.target.value })}
          className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700 font-mono" />
      </div>
      <div>
        <label className="block text-slate-400 mb-1">Convidado Menor (R$)</label>
        <input data-testid="input-event-minor-price" type="number" step="0.01" min="0"
          value={form.minor_guest_price}
          onChange={(e) => setForm({ ...form, minor_guest_price: e.target.value })}
          className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700 font-mono" />
      </div>
      <div>
        <label className="block text-slate-400 mb-1">Centro de Custo</label>
        <select value={form.cost_center_id}
          onChange={(e) => setForm({ ...form, cost_center_id: e.target.value })}
          className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700">
          {(costCenters || []).map((cc) => (
            <option key={cc.id} value={cc.id}>{cc.code} - {cc.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-slate-400 mb-1">Categoria</label>
        <input data-testid="input-event-category" list="event-category-options" type="text"
          value={form.category || ""}
          onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: "" })}
          className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700"
          placeholder="Eventos & Cobranças Avulsas" />
        <datalist id="event-category-options">
          <option value="Eventos & Cobranças Avulsas" />
          {categoryOptions.map((c) => <option key={c} value={c} />)}
        </datalist>
      </div>
      <div>
        <label className="block text-slate-400 mb-1">Subcategoria</label>
        <input data-testid="input-event-subcategory" list="event-subcategory-options" type="text"
          value={form.subcategory || ""}
          onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
          className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700"
          placeholder="Ex: Confraternização, Rateio…" />
        <datalist id="event-subcategory-options">
          {subcategoryOptions.map((s) => <option key={s} value={s} />)}
        </datalist>
      </div>
    </div>

    {/* Tabela de participantes com adultos/menores por Irmão */}
    <div className="pt-2 border-t border-slate-800">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 text-[#D4AF37]" />
          Irmãos & Convidados
        </span>
        <span data-testid="event-selected-count" className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] font-bold">
          {selectedIds.length}
        </span>
        <span className="text-[10px] text-slate-400 font-mono ml-2">
          Total geral: <span className="text-[#D4AF37] font-bold" data-testid="event-total-preview">{fmt(totalGeral)}</span>
        </span>
        <button type="button" data-testid="btn-toggle-all-members" onClick={selectAll}
          className="ml-auto text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-300 hover:text-white uppercase">
          {selectedIds.length === members.length && members.length > 0 ? "Desmarcar Todos" : "Marcar Todos"}
        </button>
      </div>

      {members.length === 0 ? (
        <div className="text-[11px] text-slate-500 italic p-2">Nenhum Irmão cadastrado ainda.</div>
      ) : (
        <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-800">
          <table className="w-full text-left text-xs" data-testid="table-event-participants">
            <thead className="sticky top-0 bg-[#080C14] border-b border-slate-800 text-slate-400 uppercase text-[10px]">
              <tr>
                <th className="py-2 px-2 text-center w-8">✓</th>
                <th className="py-2 px-2">Irmão</th>
                <th className="py-2 px-2 text-center w-20" title="Cobrar do próprio Irmão?">Irmão Paga?</th>
                <th className="py-2 px-2 text-center w-20">Convidados</th>
                <th className="py-2 px-2 text-center w-20">Menores</th>
                <th className="py-2 px-2 text-right w-24">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {members.map((m) => {
                const p = getP(m.id);
                const selected = !!p.selected;
                return (
                  <tr key={m.id} className={selected ? "bg-[#D4AF37]/5" : "hover:bg-white/5"}>
                    <td className="py-1.5 px-2 text-center">
                      <input
                        data-testid={`chk-event-participant-${m.id}`}
                        type="checkbox"
                        checked={selected}
                        onChange={(e) => setP(m.id, { selected: e.target.checked })}
                        className="accent-[#D4AF37]"
                      />
                    </td>
                    <td className={`py-1.5 px-2 ${selected ? "text-white font-semibold" : "text-slate-400"}`}>
                      {m.name}
                    </td>
                    <td className="py-1.5 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={p.include_member !== false}
                        onChange={(e) => setP(m.id, { include_member: e.target.checked, selected: true })}
                        disabled={!selected}
                        className="accent-[#D4AF37] disabled:opacity-30"
                      />
                    </td>
                    <td className="py-1.5 px-2 text-center">
                      <input
                        data-testid={`input-adults-${m.id}`}
                        type="number" min="0"
                        value={p.adults || 0}
                        onChange={(e) => setP(m.id, { adults: e.target.value, selected: true })}
                        disabled={!selected}
                        className="w-14 bg-[#131C2E] text-white p-1 rounded border border-slate-700 text-center font-mono disabled:opacity-40"
                      />
                    </td>
                    <td className="py-1.5 px-2 text-center">
                      <input
                        data-testid={`input-minors-${m.id}`}
                        type="number" min="0"
                        value={p.minors || 0}
                        onChange={(e) => setP(m.id, { minors: e.target.value, selected: true })}
                        disabled={!selected}
                        className="w-14 bg-[#131C2E] text-white p-1 rounded border border-slate-700 text-center font-mono disabled:opacity-40"
                      />
                    </td>
                    <td className={`py-1.5 px-2 text-right font-mono font-bold ${selected ? "text-[#D4AF37]" : "text-slate-600"}`}>
                      {selected ? fmt(rowTotal(p)) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[10px] text-slate-500 mt-1.5">
        Marque quem vai, ajuste convidados adultos/menores por Irmão e o total é calculado na hora. Ao salvar, uma cobrança individual é criada pra cada linha marcada.
      </p>
    </div>

    <div className="flex justify-end gap-2 pt-1">
      <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:text-white text-xs">Cancelar</button>
      <button data-testid="btn-save-event" type="submit" disabled={busy}
        className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-1.5 rounded-lg text-xs uppercase tracking-wider disabled:opacity-40">
        {busy ? "Salvando..." : editingEvent ? "Salvar" : `Criar Evento${selectedIds.length > 0 ? ` + ${selectedIds.length} Cobrança(s)` : ""}`}
      </button>
    </div>
  </form>
  );
};

// -----------------------------------------------------------------------------
// Sub-componente: cobranças (charges) de um evento
// -----------------------------------------------------------------------------
const EventDetailView = ({ detail, members, costCenters, formatCurrency, onBack, onReload, onEdit }) => {
  const [defaultAdults, setDefaultAdults] = useState(0);
  const [defaultMinors, setDefaultMinors] = useState(0);
  const [includeMember, setIncludeMember] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingChargeId, setEditingChargeId] = useState(null);
  const [editForm, setEditForm] = useState({ adults: 0, minors: 0, include_member: true });
  const [payingChargeId, setPayingChargeId] = useState(null);
  const [payForm, setPayForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: "PIX",
    cost_center_id: "cc_01",
    amount_received: "",
    notes: "",
  });

  if (!detail) {
    return (
      <div className="p-8 text-center text-slate-400">Carregando cobranças...</div>
    );
  }

  const { event, charges } = detail;
  const chargedMemberIds = new Set(charges.map((c) => c.member_id));
  const uncharged = (members || []).filter((m) => !chargedMemberIds.has(m.id));

  const toggleId = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleBulkCharge = async (mode) => {
    // mode: "all" = todos os Irmãos sem cobrança ainda / "selected" = seleção manual
    setBusy(true);
    try {
      const payload = {
        member_ids: mode === "all" ? null : selectedIds,
        default_adults: parseInt(defaultAdults) || 0,
        default_minors: parseInt(defaultMinors) || 0,
        include_member: includeMember,
        overwrite,
      };
      const res = await axios.post(`${API}/treasury/events/${event.id}/charges`, payload);
      toast.success(`${res.data.created_or_updated} cobrança(s) geradas. ${res.data.skipped || 0} puladas.`);
      setSelectedIds([]);
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao lançar cobranças.");
    } finally {
      setBusy(false);
    }
  };

  const startEditCharge = (c) => {
    setEditingChargeId(c.id);
    setEditForm({ adults: c.adults, minors: c.minors, include_member: c.include_member });
  };

  const saveEditCharge = async (c) => {
    try {
      await axios.patch(`${API}/treasury/event-charges/${c.id}`, {
        adults: parseInt(editForm.adults) || 0,
        minors: parseInt(editForm.minors) || 0,
        include_member: !!editForm.include_member,
      });
      toast.success("Cobrança atualizada.");
      setEditingChargeId(null);
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao editar.");
    }
  };

  const removeCharge = async (c) => {
    if (!window.confirm(`Remover a cobrança de ${c.member_name}?`)) return;
    try {
      await axios.delete(`${API}/treasury/event-charges/${c.id}`);
      toast.success("Cobrança removida.");
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao remover.");
    }
  };

  const openPay = (c) => {
    setPayingChargeId(c.id);
    setPayForm({
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: "PIX",
      cost_center_id: c.cost_center_id || event.cost_center_id || "cc_01",
      amount_received: (c.remaining_amount || c.total_amount || 0).toFixed(2),
      notes: "",
    });
  };

  const submitPay = async (e, charge) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/treasury/event-charges/${charge.id}/pay`, {
        ...payForm,
        amount_received: parseFloat(payForm.amount_received) || 0,
      });
      const msg = res.data.is_full
        ? `Cobrança quitada! ${res.data.overpay > 0 ? `Excedente de R$ ${res.data.overpay.toFixed(2)} → crédito.` : ""}`
        : `Pagamento PARCIAL. Restante: R$ ${res.data.remaining_amount.toFixed(2)}`;
      toast.success(msg);
      setPayingChargeId(null);
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao registrar pagamento.");
    } finally {
      setBusy(false);
    }
  };

  const paidCount = charges.filter((c) => c.status === "pago").length;
  const partialCount = charges.filter((c) => c.status === "parcial").length;
  const openCount = charges.filter((c) => c.status === "em_aberto").length;
  const totalReceived = charges.reduce((s, c) => s + Number(c.paid_amount || c.partial_paid_amount || 0), 0);
  const totalOpen = charges.filter((c) => c.status !== "pago").reduce((s, c) => s + Math.max(0, Number(c.total_amount || 0) - Number(c.partial_paid_amount || 0)), 0);
  const totalCharged = charges.reduce((s, c) => s + Number(c.total_amount || 0), 0);
  const totalAdults = charges.reduce((s, c) => s + Number(c.adults || 0), 0);
  const totalMinors = charges.reduce((s, c) => s + Number(c.minors || 0), 0);
  const totalGuests = totalAdults + totalMinors;
  const totalMembersCharged = charges.filter((c) => c.include_member).length;

  const downloadClosingPDF = async () => {
    const src = document.getElementById("event-closing-print");
    if (!src) return toast.error("Bloco de fechamento não encontrado.");
    const safeName = (event.name || "evento").replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");
    const filename = `Fechamento_${safeName}.pdf`;
    const wrapper = document.createElement("div");
    wrapper.setAttribute("style", "position: fixed; top: 0; left: 0; width: 0; height: 0; overflow: hidden; opacity: 0; pointer-events: none;");
    const clone = src.cloneNode(true);
    const originalParent = src.parentNode;
    const originalNext = src.nextSibling;
    if (originalParent) originalParent.removeChild(src);
    clone.setAttribute("style", "position: static; width: 794px; min-height: 200px; background:#fff; color:#111; padding:24px; font-family: Georgia,'Times New Roman',serif; opacity:1; visibility:visible; box-shadow:none;");
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);
    toast.message("Gerando PDF de fechamento...");
    try {
      await html2pdf().set({
        margin: [10,10,10,10], filename,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false, width: 794, windowWidth: 794 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css","legacy"] },
      }).from(clone).save();
      toast.success("Fechamento baixado!");
    } catch (err) {
      toast.error("Falha ao gerar PDF: " + (err?.message || ""));
    } finally {
      if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
      if (originalParent) {
        if (originalNext) originalParent.insertBefore(src, originalNext);
        else originalParent.appendChild(src);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* header do evento */}
      <div className="luxury-card p-4 border-l-4 border-l-purple-500 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800" data-testid="btn-back-to-events">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h3 className="font-serif font-bold text-white text-base flex items-center gap-2">
              <Gift className="w-4 h-4 text-purple-400" />
              {event.name}
            </h3>
            <p className="text-[10px] text-slate-400 mt-0.5">
              {event.event_date && `Evento em ${formatBRDate(event.event_date)}`}
              {event.due_date && ` • Vence em ${formatBRDate(event.due_date)}`}
              {` • Valores: Irmão ${formatCurrency(event.member_price)} / Adulto ${formatCurrency(event.adult_guest_price)} / Menor ${formatCurrency(event.minor_guest_price)}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-right">
          <div>
            <div className="text-[10px] text-slate-500 uppercase">Recebido</div>
            <div className="text-emerald-400 font-mono font-bold text-sm" data-testid="event-total-received">{formatCurrency(totalReceived)}</div>
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase">A Receber</div>
            <div className="text-[#D4AF37] font-mono font-bold text-sm" data-testid="event-total-open">{formatCurrency(totalOpen)}</div>
          </div>
          <button onClick={onEdit} className="p-2 rounded-lg border border-slate-700 text-slate-300 hover:text-white hover:border-[#D4AF37]" title="Editar evento">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            data-testid="btn-event-closing-pdf"
            onClick={downloadClosingPDF}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/25 text-xs font-semibold"
            title="Baixar relatório de fechamento em PDF"
          >
            <FileDown className="w-3.5 h-3.5" />
            Fechamento
          </button>
        </div>
      </div>

      {/* Lançamento em lote — só aparece completo quando ainda não há cobranças; após lançar, vira botão colapsável */}
      {charges.length === 0 ? (
        <div className="luxury-card p-4 border border-slate-800 space-y-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs uppercase font-bold tracking-wider text-[#D4AF37]">Lançar Cobranças</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <div>
              <label className="text-slate-400 mb-1 block">Adultos p/ Irmão</label>
              <input
                data-testid="input-default-adults"
                type="number" min="0" value={defaultAdults}
                onChange={(e) => setDefaultAdults(e.target.value)}
                className="w-full bg-[#131C2E] text-white p-1.5 rounded border border-slate-700 font-mono text-center"
              />
            </div>
            <div>
              <label className="text-slate-400 mb-1 block">Menores p/ Irmão</label>
              <input
                data-testid="input-default-minors"
                type="number" min="0" value={defaultMinors}
                onChange={(e) => setDefaultMinors(e.target.value)}
                className="w-full bg-[#131C2E] text-white p-1.5 rounded border border-slate-700 font-mono text-center"
              />
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-1.5 text-slate-300 cursor-pointer">
                <input type="checkbox" checked={includeMember} onChange={(e) => setIncludeMember(e.target.checked)} className="accent-[#D4AF37]" />
                Cobrar do Irmão
              </label>
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-1.5 text-slate-300 cursor-pointer">
                <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} className="accent-amber-500" />
                Sobrescrever existentes
              </label>
            </div>
            <div className="flex items-end gap-1">
              <button
                data-testid="btn-charge-all"
                type="button"
                onClick={() => handleBulkCharge("all")}
                disabled={busy}
                className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-2 py-1.5 rounded text-[10px] uppercase disabled:opacity-40"
              >
                Lançar p/ Todos
              </button>
            </div>
          </div>

          {/* Seleção manual */}
          {uncharged.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-400 hover:text-white">Ou lance apenas para Irmãos específicos ({uncharged.length} sem cobrança)</summary>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1.5 p-2 bg-[#080C14] rounded border border-slate-800 max-h-48 overflow-y-auto">
                {uncharged.map((m) => (
                  <label key={m.id} className="flex items-center gap-1.5 text-slate-300 cursor-pointer p-1 hover:bg-white/5 rounded">
                    <input type="checkbox" checked={selectedIds.includes(m.id)} onChange={() => toggleId(m.id)} className="accent-[#D4AF37]" />
                    <span className="truncate">{m.name}</span>
                  </label>
                ))}
              </div>
              {selectedIds.length > 0 && (
                <button
                  data-testid="btn-charge-selected"
                  type="button"
                  onClick={() => handleBulkCharge("selected")}
                  disabled={busy}
                  className="mt-2 bg-emerald-500/20 border border-emerald-500 text-emerald-300 hover:bg-emerald-500/30 font-bold px-3 py-1 rounded text-[10px] uppercase disabled:opacity-40"
                >
                  Lançar para {selectedIds.length} selecionado(s)
                </button>
              )}
            </details>
          )}
        </div>
      ) : uncharged.length > 0 ? (
        <details className="luxury-card border border-slate-800 rounded-xl">
          <summary
            data-testid="btn-toggle-add-charges"
            className="cursor-pointer p-3 flex items-center justify-between hover:bg-white/5 rounded-xl"
          >
            <div className="flex items-center gap-2 text-xs">
              <Users className="w-4 h-4 text-[#D4AF37]" />
              <span className="uppercase font-bold tracking-wider text-slate-300">Adicionar cobranças para os {uncharged.length} Irmão(s) restantes</span>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Expandir ▾</span>
          </summary>
          <div className="p-4 border-t border-slate-800 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              <div>
                <label className="text-slate-400 mb-1 block">Adultos p/ Irmão</label>
                <input type="number" min="0" value={defaultAdults} onChange={(e) => setDefaultAdults(e.target.value)} className="w-full bg-[#131C2E] text-white p-1.5 rounded border border-slate-700 font-mono text-center" />
              </div>
              <div>
                <label className="text-slate-400 mb-1 block">Menores p/ Irmão</label>
                <input type="number" min="0" value={defaultMinors} onChange={(e) => setDefaultMinors(e.target.value)} className="w-full bg-[#131C2E] text-white p-1.5 rounded border border-slate-700 font-mono text-center" />
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-1.5 text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={includeMember} onChange={(e) => setIncludeMember(e.target.checked)} className="accent-[#D4AF37]" />
                  Cobrar do Irmão
                </label>
              </div>
              <div />
              <div className="flex items-end gap-1">
                <button
                  type="button"
                  onClick={() => handleBulkCharge("all")}
                  disabled={busy}
                  className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-2 py-1.5 rounded text-[10px] uppercase disabled:opacity-40"
                >
                  Lançar p/ Restantes
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 p-2 bg-[#080C14] rounded border border-slate-800 max-h-48 overflow-y-auto">
              {uncharged.map((m) => (
                <label key={m.id} className="flex items-center gap-1.5 text-slate-300 cursor-pointer p-1 hover:bg-white/5 rounded text-xs">
                  <input type="checkbox" checked={selectedIds.includes(m.id)} onChange={() => toggleId(m.id)} className="accent-[#D4AF37]" />
                  <span className="truncate">{m.name}</span>
                </label>
              ))}
            </div>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => handleBulkCharge("selected")}
                disabled={busy}
                className="bg-emerald-500/20 border border-emerald-500 text-emerald-300 hover:bg-emerald-500/30 font-bold px-3 py-1.5 rounded text-[10px] uppercase disabled:opacity-40"
              >
                Lançar para {selectedIds.length} selecionado(s)
              </button>
            )}
          </div>
        </details>
      ) : null}

      {/* Tabela de cobranças */}
      <div className="luxury-card border border-slate-800 rounded-xl overflow-hidden">
        <div className="p-3 border-b border-slate-800 flex items-center justify-between">
          <span className="text-xs uppercase font-bold tracking-wider text-slate-400">
            Cobranças ({charges.length}) — <span className="text-emerald-400">{paidCount} pago(s)</span> · <span className="text-amber-400">{charges.length - paidCount} pendente(s)</span>
          </span>
        </div>
        {charges.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm" data-testid="event-charges-empty">
            Nenhuma cobrança lançada. Use "Lançar p/ Todos" acima para gerar em massa.
          </div>
        ) : (
          <table className="w-full text-left text-xs" data-testid="table-event-charges">
            <thead>
              <tr className="bg-[#080C14] border-b border-slate-800 text-slate-400 uppercase">
                <th className="py-2.5 px-3">Irmão</th>
                <th className="py-2.5 px-3 text-center">Convidados</th>
                <th className="py-2.5 px-3 text-center">Menores</th>
                <th className="py-2.5 px-3 text-center">Irmão Paga?</th>
                <th className="py-2.5 px-3 text-right">Total</th>
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-3 text-center">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {charges.map((c) => {
                const isPaid = c.status === "pago";
                const isPartial = c.status === "parcial";
                const isEditing = editingChargeId === c.id;
                const isPaying = payingChargeId === c.id;
                return (
                  <React.Fragment key={c.id}>
                    <tr className="hover:bg-white/5">
                      <td className="py-2.5 px-3 font-semibold text-white">
                        {c.member_name}
                        <div className="text-[9px] text-slate-500 font-mono">CIM {c.cim}</div>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {isEditing ? (
                          <input type="number" min="0" value={editForm.adults} onChange={(e) => setEditForm({ ...editForm, adults: e.target.value })} className="w-14 bg-[#080C14] text-white p-1 rounded border border-[#D4AF37]/40 text-center font-mono" />
                        ) : (c.adults || 0)}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {isEditing ? (
                          <input type="number" min="0" value={editForm.minors} onChange={(e) => setEditForm({ ...editForm, minors: e.target.value })} className="w-14 bg-[#080C14] text-white p-1 rounded border border-[#D4AF37]/40 text-center font-mono" />
                        ) : (c.minors || 0)}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {isEditing ? (
                          <input type="checkbox" checked={!!editForm.include_member} onChange={(e) => setEditForm({ ...editForm, include_member: e.target.checked })} className="accent-[#D4AF37]" />
                        ) : (c.include_member ? "Sim" : "Não")}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono font-bold text-[#D4AF37]">
                        {formatCurrency(c.total_amount)}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {isPaid ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px] font-bold">
                            <CheckCircle2 className="w-3 h-3" /> Pago
                          </span>
                        ) : isPartial ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/40 text-[10px] font-bold">
                            <Coins className="w-3 h-3" /> Parcial (falta {formatCurrency(c.remaining_amount)})
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 text-[10px] font-semibold">
                            <Clock className="w-3 h-3" /> Em Aberto
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          {isEditing ? (
                            <>
                              <button onClick={() => saveEditCharge(c)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold">
                                <Save className="w-3 h-3" /> Salvar
                              </button>
                              <button onClick={() => setEditingChargeId(null)} className="px-2 py-1 rounded bg-slate-800 text-slate-300 text-[10px]">Cancelar</button>
                            </>
                          ) : (
                            <>
                              {!isPaid && (
                                <button
                                  data-testid={`btn-pay-charge-${c.id}`}
                                  onClick={() => openPay(c)}
                                  className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-2.5 py-1 rounded text-[10px]"
                                >
                                  {isPartial ? "Continuar" : "Pagar"}
                                </button>
                              )}
                              {!isPaid && (
                                <button onClick={() => startEditCharge(c)} title="Editar convidados" className="p-1 rounded border border-slate-700 text-slate-300 hover:text-white hover:border-[#D4AF37]">
                                  <Pencil className="w-3 h-3" />
                                </button>
                              )}
                              <button onClick={() => removeCharge(c)} title="Remover cobrança" className="p-1 rounded border border-rose-500/30 text-rose-400 hover:bg-rose-500/10">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isPaying && (
                      <tr>
                        <td colSpan={7} className="p-3 bg-[#080C14] border-t border-[#D4AF37]/30">
                          <form onSubmit={(e) => submitPay(e, c)} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
                            <div>
                              <label className="text-[10px] text-slate-400 block mb-0.5">Data</label>
                              <input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} className="w-full bg-[#131C2E] text-white p-1.5 rounded border border-slate-700 text-xs" required />
                            </div>
                            <div>
                              <label className="text-[10px] text-slate-400 block mb-0.5">Valor Recebido</label>
                              <input data-testid={`input-pay-charge-amount-${c.id}`} type="number" step="0.01" min="0" value={payForm.amount_received} onChange={(e) => setPayForm({ ...payForm, amount_received: e.target.value })} className="w-full bg-[#131C2E] text-white p-1.5 rounded border border-[#D4AF37]/40 text-xs font-mono" />
                            </div>
                            <div>
                              <label className="text-[10px] text-slate-400 block mb-0.5">Forma</label>
                              <select value={payForm.payment_method} onChange={(e) => setPayForm({ ...payForm, payment_method: e.target.value })} className="w-full bg-[#131C2E] text-white p-1.5 rounded border border-slate-700 text-xs">
                                <option value="PIX">PIX</option>
                                <option value="Boleto">Boleto</option>
                                <option value="Dinheiro">Dinheiro</option>
                                <option value="Transferência TED">TED</option>
                              </select>
                            </div>
                            <div>
                              <label className="text-[10px] text-slate-400 block mb-0.5">Centro de Custo</label>
                              <select value={payForm.cost_center_id} onChange={(e) => setPayForm({ ...payForm, cost_center_id: e.target.value })} className="w-full bg-[#131C2E] text-white p-1.5 rounded border border-slate-700 text-xs">
                                {(costCenters || []).map((cc) => (
                                  <option key={cc.id} value={cc.id}>{cc.code} - {cc.name}</option>
                                ))}
                              </select>
                            </div>
                            <div className="flex gap-1">
                              <button type="button" onClick={() => setPayingChargeId(null)} className="flex-1 px-2 py-1.5 rounded bg-slate-800 text-slate-300 hover:text-white text-xs">Cancelar</button>
                              <button data-testid={`btn-confirm-pay-charge-${c.id}`} type="submit" disabled={busy} className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-2 py-1.5 rounded text-xs disabled:opacity-40">Confirmar</button>
                            </div>
                          </form>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ============ PDF de Fechamento (off-screen) ============ */}
      <div id="event-closing-print" style={{ position: "absolute", left: "-99999px", top: 0, width: "210mm", background: "#fff", padding: 0 }}>
        <style>{`
          #event-closing-print { font-family: Georgia,'Times New Roman',serif; color:#111; }
          #event-closing-print h1 { font-size:22px; margin:0 0 4px 0; }
          #event-closing-print h2 { font-size:14px; margin:18px 0 6px 0; padding-bottom:4px; border-bottom:1.5px solid #6d28d9; color:#6d28d9; text-transform:uppercase; letter-spacing:1px; }
          #event-closing-print .meta { font-size:11px; color:#444; }
          #event-closing-print table.rpt { width:100%; border-collapse:collapse; font-size:11px; margin-top:4px; }
          #event-closing-print table.rpt th { background:#ede9fe; color:#4c1d95; text-align:left; padding:6px 8px; border:1px solid #c4b5fd; font-size:10px; text-transform:uppercase; letter-spacing:0.4px; }
          #event-closing-print table.rpt td { padding:5px 8px; border:1px solid #e5e5e5; }
          #event-closing-print table.rpt td.num { text-align:right; font-family:'Courier New',monospace; }
          #event-closing-print table.rpt tr.paid { background:#eaf7ed; }
          #event-closing-print table.rpt tr.partial { background:#eef4fb; }
          #event-closing-print .cards { display:flex; gap:12px; margin-top:12px; flex-wrap:wrap; }
          #event-closing-print .cards .card { flex:1; min-width:120px; border:1px solid #c4b5fd; padding:8px 10px; border-radius:4px; }
          #event-closing-print .cards .card b { display:block; font-size:10px; color:#5b21b6; text-transform:uppercase; margin-bottom:4px; }
          #event-closing-print .cards .card span { font-family:'Courier New',monospace; font-size:14px; font-weight:bold; }
          #event-closing-print .footer { margin-top:40px; display:flex; justify-content:space-between; font-size:10px; color:#666; }
          #event-closing-print .sign { border-top:1px solid #333; text-align:center; width:220px; padding-top:4px; }
        `}</style>
        <div style={{ borderBottom: "2px solid #6d28d9", paddingBottom: 10 }}>
          <h1>Fechamento de Evento</h1>
          <div className="meta"><strong>{event.name}</strong>{event.description ? ` • ${event.description}` : ""}</div>
          <div className="meta" style={{ marginTop: 4 }}>
            {event.event_date && <><strong>Data do evento:</strong> {formatBRDate(event.event_date)} • </>}
            {event.due_date && <><strong>Vencimento:</strong> {formatBRDate(event.due_date)} • </>}
            <strong>Emitido em:</strong> {formatBRDate(new Date().toISOString().slice(0, 10))}
          </div>
          <div className="meta" style={{ marginTop: 4 }}>
            Valores: Irmão {formatCurrency(event.member_price)} • Convidado Adulto {formatCurrency(event.adult_guest_price)} • Convidado Menor {formatCurrency(event.minor_guest_price)}
          </div>
        </div>

        <div className="cards">
          <div className="card"><b>Total Cobrado</b><span style={{ color: "#5b21b6" }}>{formatCurrency(totalCharged)}</span></div>
          <div className="card"><b>Recebido</b><span style={{ color: "#047857" }}>{formatCurrency(totalReceived)}</span></div>
          <div className="card"><b>A Receber</b><span style={{ color: "#8B6F1F" }}>{formatCurrency(totalOpen)}</span></div>
          <div className="card"><b>Irmãos Cobrados</b><span>{totalMembersCharged}</span></div>
          <div className="card"><b>Convidados Adultos</b><span>{totalAdults}</span></div>
          <div className="card"><b>Convidados Menores</b><span>{totalMinors}</span></div>
          <div className="card"><b>Total de Convidados</b><span style={{ color: "#5b21b6" }}>{totalGuests}</span></div>        </div>

        <h2>Status das Cobranças</h2>
        <div style={{ display: "flex", gap: 8, marginTop: 6, fontSize: 11 }}>
          <span style={{ background: "#eaf7ed", padding: "3px 8px", borderRadius: 3 }}>✓ Pagos: <strong>{paidCount}</strong></span>
          <span style={{ background: "#eef4fb", padding: "3px 8px", borderRadius: 3 }}>◐ Parciais: <strong>{partialCount}</strong></span>
          <span style={{ background: "#fef3c7", padding: "3px 8px", borderRadius: 3 }}>○ Em aberto: <strong>{openCount}</strong></span>
        </div>

        <h2>Detalhamento por Irmão ({charges.length})</h2>
        <table className="rpt">
          <thead>
            <tr>
              <th>Irmão</th>
              <th>CIM</th>
              <th style={{ textAlign: "center" }}>Irmão Paga?</th>
              <th style={{ textAlign: "center" }}>Convidados</th>
              <th style={{ textAlign: "center" }}>Menores</th>
              <th style={{ textAlign: "right" }}>Total</th>
              <th style={{ textAlign: "right" }}>Pago</th>
              <th style={{ textAlign: "right" }}>Falta</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {charges.map((c) => {
              const st = c.status;
              const rowClass = st === "pago" ? "paid" : st === "parcial" ? "partial" : "";
              const paid = Number(c.paid_amount || c.partial_paid_amount || 0);
              const falta = st === "pago" ? 0 : Math.max(0, Number(c.total_amount || 0) - Number(c.partial_paid_amount || 0));
              const stLabel = st === "pago" ? "Pago" : st === "parcial" ? "Parcial" : "Em Aberto";
              return (
                <tr key={c.id} className={rowClass}>
                  <td>{c.member_name}</td>
                  <td>{c.cim}</td>
                  <td style={{ textAlign: "center" }}>{c.include_member ? "Sim" : "Não"}</td>
                  <td style={{ textAlign: "center" }}>{c.adults || 0}</td>
                  <td style={{ textAlign: "center" }}>{c.minors || 0}</td>
                  <td className="num">{formatCurrency(c.total_amount)}</td>
                  <td className="num" style={{ color: "#047857" }}>{formatCurrency(paid)}</td>
                  <td className="num" style={{ color: falta > 0 ? "#be123c" : "#111" }}>{formatCurrency(falta)}</td>
                  <td style={{ fontSize: 10 }}>{stLabel}{st === "pago" && c.paid_at ? ` em ${formatBRDate(c.paid_at)}` : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="footer">
          <div>Documento emitido pela Tesouraria • Lojas Integradas</div>
          <div className="sign">Tesoureiro</div>
        </div>
      </div>
    </div>
  );
};
